import { AnimatePresence, motion } from 'framer-motion';
import type { ActionAlert } from '~/types/actions';

interface Props {
  alert: ActionAlert;
  clearAlert: () => void;
  postMessage: (message: string) => void;
}

export default function ChatAlert({ alert, clearAlert, postMessage }: Props) {
  const { description, content, source } = alert;

  const isPreview = source === 'preview';
  const title = isPreview ? 'Preview Error' : 'Terminal Error';
  const message = isPreview
    ? 'We encountered an error while running the preview. Would you like Dunea to analyze and help resolve this issue?'
    : 'We encountered an error while running terminal commands. Would you like Dunea to analyze and help resolve this issue?';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.2 }}
        className="rounded-lg border border-danger/30 bg-danger/[0.05] p-3.5 mb-2"
      >
        <div className="flex items-start gap-3">
          <span className="i-ph:warning text-danger text-[16px] flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h3 className="text-[13px] font-medium text-primary">{title}</h3>
            <p className="mt-1 text-[12.5px] text-secondary">{message}</p>
            {description && (
              <pre className="mt-2.5 mb-1 px-2.5 py-2 rounded-md bg-sunken border border-hairline font-geist-mono text-[11.5px] text-muted whitespace-pre-wrap break-words">
                {description}
              </pre>
            )}
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={() =>
                  postMessage(
                    `*Fix this ${isPreview ? 'preview' : 'terminal'} error* \n\`\`\`${isPreview ? 'js' : 'sh'}\n${content}\n\`\`\`\n`,
                  )
                }
                className="ui-btn-accent !px-3.5 !py-1.5 !text-[12px] flex items-center gap-1.5"
              >
                <span className="i-ph:chat-circle text-[12px]" />
                Ask Dunea
              </button>
              <button type="button" onClick={clearAlert} className="ui-btn-ghost">
                Dismiss
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
